/**
 * Compartir un secreto puntual con otro usuario: sobre cifrado a su clave
 * pública (ECIES). Alternativa al P2P-QR cuando el receptor no está presente.
 *
 * El emisor solo necesita la clave PÚBLICA del receptor (la publica el server).
 * El servidor reenvía el sobre opaco; no puede leerlo. Opcionalmente el sobre
 * expira (metadato en claro para que el server lo purgue, pero el contenido
 * sigue siendo ilegible para él).
 */

import { openWithPrivateKey, type SealedToPublicKey, sealToPublicKey } from "./asymmetric";
import { utf8Decode, utf8Encode } from "./encoding";
import type { VaultItemPlain } from "./types";

const AAD_SHARE = "boveda:shared-secret:v1";

export interface SharedSecret {
  readonly sealed: SealedToPublicKey;
  readonly fromUserId: string;
  readonly toUserId: string;
  /** ISO opcional; el server puede purgar tras esta fecha (metadato no sensible). */
  readonly expiresAt?: string;
}

export async function shareSecret(
  fromUserId: string,
  toUserId: string,
  recipientPublicKey: string,
  secret: VaultItemPlain,
  expiresAt?: string,
): Promise<SharedSecret> {
  const sealed = await sealToPublicKey(
    recipientPublicKey,
    utf8Encode(JSON.stringify(secret)),
    AAD_SHARE,
  );
  return { sealed, fromUserId, toUserId, expiresAt };
}

export async function openSharedSecret(
  recipientPrivateKey: string,
  shared: SharedSecret,
): Promise<VaultItemPlain> {
  const bytes = await openWithPrivateKey(recipientPrivateKey, shared.sealed, AAD_SHARE);
  // Trust boundary: el sobre lo selló el emisor (AEAD ya autenticó el contenido).
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  return JSON.parse(utf8Decode(bytes)) as VaultItemPlain;
}
